import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase, isOnline } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // null = still loading, false = no household, object = has household
  const [membership, setMembership] = useState(null);

  const fetchMembership = useCallback(async () => {
    if (!isOnline()) {
      console.log('[Auth] offline, skipping membership');
      setMembership(false);
      return;
    }
    console.log('[Auth] fetching membership...');
    const { data, error } = await supabase.rpc('get_my_membership');
    console.log('[Auth] membership RPC result:', JSON.stringify({ data, error }));
    if (error) {
      console.error('Failed to fetch membership:', error.message);
      setMembership(false);
      return;
    }
    setMembership(data || false);
  }, []);

  const refreshMembership = useCallback(async () => {
    await fetchMembership();
  }, [fetchMembership]);

  // Sync-only: track auth session
  useEffect(() => {
    if (!isOnline()) { setLoading(false); return; }

    let settled = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        settled = true;
        console.log('[Auth] onAuthStateChange:', event, !!session);
        setSession(session);
        if (!session) {
          setMembership(false);
          setLoading(false);
        }
      }
    );

    // Safety timeout — if auth never responds, stop blocking the app
    const timeout = setTimeout(() => {
      if (!settled) {
        console.warn('[Auth] timed out waiting for auth state, falling back to logged-out');
        setLoading(false);
      }
    }, 5000);

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  // Fetch membership when user changes (separate from auth listener)
  const userId = session?.user?.id;
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    fetchMembership()
      .catch((e) => {
        console.error('Membership fetch failed:', e);
        if (!cancelled) setMembership(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [userId, fetchMembership]);

  const signInWithEmail = async (email) => {
    if (!isOnline()) return { error: { message: 'No connection to server' } };
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (!error) trackEvent('Login');
    return { error };
  };

  const signUpWithPassword = async (email, password) => {
    if (!isOnline()) return { error: { message: 'No connection to server' } };
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    if (!error) trackEvent('SignUp');
    // With email confirmations on, signUp returns no session — the user must
    // confirm before they can sign in.
    return { error, needsConfirmation: !error && !data.session };
  };

  const signInWithPassword = async (email, password) => {
    if (!isOnline()) return { error: { message: 'No connection to server' } };
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) trackEvent('Login');
    return { error };
  };

  const resetPassword = async (email) => {
    if (!isOnline()) return { error: { message: 'No connection to server' } };
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    return { error };
  };

  const updatePassword = async (password) => {
    if (!isOnline()) return { error: { message: 'No connection to server' } };
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  };

  const signOut = async () => {
    if (!isOnline()) return;
    const { error } = await supabase.auth.signOut();
    if (error) console.error('Sign out error:', error.message);
    setMembership(false);
  };

  const value = {
    session,
    user: session?.user ?? null,
    loading,
    signInWithEmail,
    signUpWithPassword,
    signInWithPassword,
    resetPassword,
    updatePassword,
    signOut,
    membership,
    householdId: membership ? membership.household_id : null,
    currentMember: membership ? { id: membership.member_id, name: membership.member_name, type: membership.member_type } : null,
    inviteCode: membership ? membership.invite_code : null,
    refreshMembership,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
